import { AxiosError } from 'axios';
import { FC } from 'react';

import { Flex, Text } from '@mantine/core';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { bookingApi } from '@/shared/api/booking';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store';
import { IRequestError } from '@/shared/types/app.types.ts';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

type ActiveBookingActionsProps = {
	bookingId: number;
	type: 'cancel' | 'end';
	opened: boolean;
	close: () => void;
	closeParent?: () => void;
};

export const ActiveBookingActions: FC<ActiveBookingActionsProps> = ({
	bookingId,
	type,
	opened,
	close,
	closeParent,
}) => {
	const queryClient = useQueryClient();
	const isMobile = useAppStore((state) => state.isMobile);
	const { mutate: finishBooking, isPending: isFinishingBooking } = useMutation({
		mutationFn: () => bookingApi.finishBooking(bookingId),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['bookings'] });
			close();
			useShowNotifaction({ text: 'Бронирование завершено', type: 'success' });
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({ text: error.response?.data.message ?? '', type: 'error' });
		},
	});

	const { mutate: cancelBooking, isPending: isCancelingBooking } = useMutation({
		mutationFn: () => bookingApi.deleteBooking(bookingId),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['bookings'] });
			close();
			useShowNotifaction({ text: 'Бронирование отменено', type: 'success' });
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data.message ?? 'Ошибка клиент-серверного взаимодействия',
				type: 'error',
			});
		},
	});

	const handleEndBooking = () => {
		if (type === 'cancel') {
			cancelBooking();
		} else {
			finishBooking();
		}

		if (closeParent) {
			close();
			closeParent();
		}
	};

	const buttons = [
		{
			children: `${type === 'cancel' ? 'Отменить' : 'Завершить'} бронирование`,
			variant: 'default',
			loading: isFinishingBooking || isCancelingBooking,
			onClick: handleEndBooking,
		},
		{
			children: 'Закрыть',
			disabled: isFinishingBooking || isCancelingBooking,
			onClick: close,
		},
	];

	const content = (
		<Flex direction="column" align="center" justify="center" gap={8}>
			<Text ta="center" fz={20} fw={800}>
				Вы уверены, что хотите {type === 'cancel' ? 'отменить' : 'завершить'} бронирование?
			</Text>
			<Text ta="center" fz={16} fw={600}>
				Забронированное место станет доступно всем желающим
			</Text>
		</Flex>
	);

	if (isMobile)
		return (
			<BottomSheet
				opened={opened}
				onClose={close}
				title={
					<Text ta="center" fz={22} fw={800}>
						{type === 'cancel' ? 'Отмена' : 'Завершение'}
					</Text>
				}
				buttons={buttons}
				buttonsDirection="column"
				withCloseButton={false}
				withBackButton={false}
			>
				{content}
			</BottomSheet>
		);

	return (
		<ModalSheet
			opened={opened}
			onClose={close}
			title={
				<Text ta="center" fz={22} fw={800}>
					{type === 'cancel' ? 'Отмена' : 'Завершение'}
				</Text>
			}
			buttons={buttons}
			buttonsDirection="column"
			withCloseButton={false}
			centered
		>
			{content}
		</ModalSheet>
	);
};
